// src/pages/ChangePassword.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiLock,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { authHeader } from "../utils/auth";
import { API_BASE } from "../config";
import { sanitizeString, validateRequired } from "../utils/inputValidation";
import { getUserFriendlyError, ERROR_MESSAGES } from "../utils/errorMessages";

export default function ChangePassword() {
  const blue = "#3366CC";
  const text = "#0F172A";
  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSuccessArea, setShowSuccessArea] = useState(false);

  const oldRef = useRef(null);

  const responsiveCSS = `
  * { box-sizing: border-box; }
  
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }

  .page-container {
    width: 100% !important;
    overflow-x: hidden;
    background: #fff;
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }

    .desktop-nav { 
      display: none !important; 
    }

    .hamburger-btn { 
      display: inline-flex !important; 
    }

    .sticky-nav {
      height: 60px !important;
      padding: 0 16px !important;
    }
    
    .grid-2 { 
      grid-template-columns: 1fr !important; 
      gap: 24px !important; 
    }

    .account-menu-panel {
      right: 10px !important;
      top: 50px !important;
      width: calc(100vw - 20px) !important;
      max-width: 300px !important;
    }

    .hero { 
      padding: 60px 20px !important; 
      min-height: auto !important;
    }

    .hero-title { 
      font-size: 32px !important; 
      text-align: center !important; 
      width: 100%;
    }

    .cta-buttons { 
      flex-direction: column !important; 
      width: 100% !important; 
      gap: 12px !important;
    }

    .cta-buttons > button { 
      width: 100% !important; 
      margin: 0 !important;
    }

    .what-is, #why-smartclick, #cta-card, #free-trial + section {
      padding: 40px 20px !important;
    }
  }

  [id] { scroll-margin-top: 70px; }
`;

  // ใช้ authHeader จาก utils/auth.js

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setShowSuccessArea(false);

    // Sanitize และ validate input
    const sanitizedOld = sanitizeString(oldPassword);
    const sanitizedNew = sanitizeString(newPassword);
    const sanitizedConfirm = sanitizeString(confirmPassword);

    // Validate required fields
    const oldValidation = validateRequired(sanitizedOld, "Current password");
    const newValidation = validateRequired(sanitizedNew, "New password");
    const confirmValidation = validateRequired(sanitizedConfirm, "Confirm password");

    if (!oldValidation.isValid) {
      setError(oldValidation.error);
      return;
    }

    if (!newValidation.isValid) {
      setError(newValidation.error);
      return;
    }

    if (!confirmValidation.isValid) {
      setError(confirmValidation.error);
      return;
    }

    // Validate password length
    if (sanitizedNew.length < 8) {
      setError(ERROR_MESSAGES.WEAK_PASSWORD);
      return;
    }

    // Validate password match
    if (sanitizedNew !== sanitizedConfirm) {
      setError(ERROR_MESSAGES.PASSWORD_MISMATCH);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify({
          old_password: sanitizedOld,
          new_password: sanitizedNew,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // ใช้ generic error message
        throw new Error(getUserFriendlyError(res, "save"));
      }

      setSuccess(data?.message || "Password changed successfully.");
      setShowSuccessArea(true);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      oldRef.current?.focus();
    } catch (err) {
      setError(getUserFriendlyError(err, "save"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{responsiveCSS}</style>
      <div
        style={{
          width: "100%",
          background: "#fff",
          overflow: "visible",
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
        }}
      >
        {/* Sticky Header */}
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        {/* Hero */}
        <section style={{ background: "#0E2B55", color: "#fff", padding: "28px 24px 30px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: 0.2 }}>
              Change Password
            </h1>
            <p style={{ margin: "8px 0 0", color: "#93c5fd" }}>
              Update your account password to keep your Smart Audit account secure.
            </p>
          </div>
        </section>

        {/* Form */}
        <section
          style={{
            background: "#F3F6FB",
            padding: "42px 16px 80px",
          }}
        >
          <div
            style={{
              maxWidth: 640,
              margin: "0 auto",
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #E5E7EB",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              padding: "24px 24px 28px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: "rgba(59,130,246,0.14)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#1D4ED8",
                }}
              >
                <FiLock />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#0F172A" }}>
                  Change your password
                </div>
                <div style={{ fontSize: 13, color: "#64748B" }}>
                  Your new password should be at least 8 characters and unique.
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                aria-live="polite"
                style={{
                  marginBottom: 16,
                  borderRadius: 10,
                  border: "1px solid #fecaca",
                  background: "#fef2f2",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#b91c1c",
                  fontSize: 14,
                }}
              >
                <FiAlertCircle size={16} /> {error}
              </div>
            )}

            {/* Success */}
            {success && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginBottom: 16,
                  borderRadius: 10,
                  border: "1px solid #bbf7d0",
                  background: "#f0fdf4",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#166534",
                  fontSize: 14,
                }}
              >
                <FiCheckCircle size={16} /> {success}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <LabelField label="Current Password">
                <input
                  ref={oldRef}
                  type="password"
                  autoComplete="current-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  style={inputStyle}
                  placeholder="Enter your current password"
                  maxLength={128}
                />
              </LabelField>

              <LabelField label="New Password">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: newPassword
                      ? newPassword.length >= 8 ? "#22c55e" : "#f59e0b"
                      : inputStyle.borderColor,
                  }}
                  placeholder="Enter a new password"
                  maxLength={128}
                />
                {newPassword.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: newPassword.length >= 8 ? "#16a34a" : "#b45309" }}>
                    {newPassword.length >= 8 ? "✓ Meets minimum length" : `${8 - newPassword.length} more character${8 - newPassword.length === 1 ? "" : "s"} needed`}
                  </div>
                )}
              </LabelField>

              <LabelField label="Confirm New Password">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: confirmPassword
                      ? confirmPassword === newPassword ? "#22c55e" : "#ef4444"
                      : inputStyle.borderColor,
                  }}
                  placeholder="Re-type the new password"
                  maxLength={128}
                />
                {confirmPassword.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: confirmPassword === newPassword ? "#16a34a" : "#dc2626" }}>
                    {confirmPassword === newPassword ? "✓ Passwords match" : "Passwords do not match"}
                  </div>
                )}
              </LabelField>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  aria-label="Go back to previous page"
                  style={{
                    height: 40,
                    padding: "0 18px",
                    borderRadius: 8,
                    border: "1px solid #CBD5E1",
                    background: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#0F172A",
                    transition: "all 0.2s ease",
                  }}
                >
                  Back
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  aria-label={loading ? "Saving new password (please wait)" : "Save new password"}
                  style={{
                    height: 40,
                    padding: "0 24px",
                    borderRadius: 8,
                    border: "1px solid #2563EB",
                    background: loading ? "#93c5fd" : "#2563EB",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    color: "#fff",
                    minWidth: 150,
                    transition: "all 0.2s ease",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Saving..." : "Save Password"}
                </button>
              </div>
            </form>

            {showSuccessArea && (
              <div style={{ marginTop: 18, fontSize: 13, color: "#64748B" }}>
                After changing your password, make sure to use the new one next time you log in.
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

/* --------- Small helpers --------- */

function LabelField({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "96%",
  height: 40,
  borderRadius: 8,
  border: "1px solid #CBD5E1",
  padding: "0 10px",
  fontSize: 14,
  outline: "none",
  boxShadow: "inset 0 1px 2px rgba(15,23,42,0.05)",
};
